import { useState, useRef, useEffect, useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { getNotifications, markNotificationRead } from '../../services/api'
import { notificationLabel, getNotificationNavTarget } from '../../constants/notificationTypes'
import { TB_IC } from '../shell/topBarIcon'
import { AuthContext } from '../../contexts/AuthContext'

export default function NotificationBell() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Bug #6683 follow-up: signed out, the bell asked for notifications with no sign-in (a 401). Outside an
  // AuthProvider (isolated renders) the old behaviour stays.
  const auth = useContext(AuthContext)
  const signedIn = auth ? !!auth.user : true
  const { data: notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: getNotifications,
    refetchInterval: 30000,
    enabled: signedIn,
  })

  // BUG-124: clicking a notification never marked it read, so the unread
  // badge persisted forever. Mark read + refresh the shared cache first, then
  // navigate (navigation must not depend on the write succeeding).
  const markReadMutation = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const unreadCount = notifications?.filter((n) => !n.read).length || 0

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        /* The COUNT lives here, not on the glyph. Every one of the twelve
           approved figures draws the bell with a plain unread dot —
           `<div class="tb-ic">🔔<span class="dot"></span></div>`, a 7px red
           circle — and none of them draws a number. A numeral in a 16px circle
           at the top of the bar is a second thing to read in a bar whose job is
           to say where you are; the dot says the one thing the figure wants it
           to say, which is "there is something".
           Nothing is lost for anyone using a screen reader: the exact number is
           in the accessible name and the tooltip, where it is read out rather
           than squinted at. */
        aria-label={unreadCount > 0
          ? `Notifications, ${unreadCount} unread`
          : 'Notifications'}
        title={unreadCount > 0
          ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`
          : 'Notifications'}
        // The top bar is white (AppShell §6.1). The old white-on-translucent
        // values were written for the dark bar this replaced, so the bell was
        // invisible — every figure shows it.
        //
        // ROUND 7: and every figure draws it INSIDE a box, not as a bare glyph
        // — kit.css `.tb-ic`, 34×34, 1px border, 9px radius. TB_IC is that
        // rule; the dot below is `.tb-ic .dot`, 7×7, positioned inside it.
        className={`${TB_IC} text-slate-600 hover:bg-slate-50 hover:text-iflow-700`}
      >
        <Bell size={19} />
        {unreadCount > 0 && (
          <span
            data-testid="notification-dot"
            aria-hidden
            className="absolute right-[7px] top-[6px] h-[7px] w-[7px] rounded-full bg-red-500"
          />
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-[#0c4a6e]/95 backdrop-blur-md border border-white/20 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <span className="text-sm font-semibold text-white">Notifications</span>
            {unreadCount > 0 && (
              <span className="text-xs text-iflow-600">{unreadCount} new</span>
            )}
          </div>
          {notifications?.length ? (
            <div>
              {notifications.slice(0, 5).map((n) => (
                <button
                  key={n.notification_id}
                  onClick={() => {
                    if (!n.read) markReadMutation.mutate(n.notification_id)
                    navigate(getNotificationNavTarget(n))
                    setOpen(false)
                  }}
                  className={`w-full text-left px-4 py-3 hover:bg-white/5 border-b border-white/10 last:border-0 ${
                    !n.read ? 'bg-white/5' : ''
                  }`}
                >
                  <p className="text-sm text-white font-medium line-clamp-1">
                    {notificationLabel(n.type)}
                  </p>
                  <p className="text-xs text-white/60 line-clamp-2">
                    {n.org_name
                      ? `From ${n.org_name}${n.project_name ? ` · ${n.project_name}` : ''}`
                      : n.message}
                  </p>
                </button>
              ))}
              <button
                onClick={() => { navigate('/notifications'); setOpen(false) }}
                className="w-full text-center py-2 text-xs text-sky-300 hover:bg-white/5 border-t border-white/10"
              >
                View all notifications
              </button>
            </div>
          ) : (
            <div className="px-4 py-6 text-center text-sm text-white/60">No notifications</div>
          )}
        </div>
      )}
    </div>
  )
}
